/** Small shared presentational atoms for the Brisque AI surface. */
import React from 'react';
import { Channel, CHANNEL_LABELS } from '../data/types';

export const FONT = 'var(--font-primary)';

/** Render text with **bold** spans. */
/**
 * "Just now" / "5m ago" / "3d ago", falling back to a plain date once it is a week old.
 *
 * Always recomputed from the authoritative instant rather than a label stamped server-side at
 * write time, which goes stale the moment it is stored.
 */
export function relativeTime(iso: string | undefined, fallback = ''): string {
    if (!iso) return fallback;
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return fallback;
    const secs = Math.floor((Date.now() - then) / 1000);
    if (secs < 45) return 'Just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(then).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The exact moment, for a tooltip — "8 Sep 2026, 3:42 PM". A relative label is quick to scan
 *  but hides precisely when something happened; this gives the real timestamp on hover without
 *  spending a second line on it. */
export function absoluteTime(iso: string | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
}

/**
 * The compact stamp shown BESIDE the relative label — "2:17 PM" for something from today,
 * "Sep 6, 2:17 PM" once it is older.
 *
 * A tooltip was tried first and is not good enough: it needs a mouse, so it is unreachable on
 * touch, and an activity log is a record people read timestamps off directly. The day is
 * dropped for today's rows because the day heading above already says "Today" — repeating the
 * date on every row is noise.
 */
export function shortStamp(iso: string | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const sameDay = startOf(d) === startOf(new Date());
    const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    if (sameDay) return time;
    const day = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    return `${day}, ${time}`;
}

/** A day heading for grouping a list — "Today" / "Yesterday" / "8 Sep 2026". Chat products
 *  group history by day so a long list stays scannable instead of reading as one flat run. */
export function dayBucket(iso: string | undefined): string {
    if (!iso) return 'Earlier';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'Earlier';
    const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const days = Math.round((startOf(new Date()) - startOf(d)) / 86400000);
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return 'Previous 7 days';
    if (days < 30) return 'Previous 30 days';
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/** `boldColor` lets a coloured bubble (success / warning) keep its bold words in its own colour
 *  instead of the default black. */
export const Rich: React.FC<{ text: string; boldColor?: string }> = ({ text, boldColor }) => {
  const parts = (text ?? '').split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**') ? (
          <strong key={i} style={{ fontWeight: 700, color: boldColor ?? 'var(--t1)' }}>{p.slice(2, -2)}</strong>
        ) : (
          <React.Fragment key={i}>{p}</React.Fragment>
        )
      )}
    </>
  );
};

export const Icon: React.FC<{ name: string; size?: number; color?: string; style?: React.CSSProperties }> = ({ name, size = 18, color = 'inherit', style }) => (
  <span className="material-symbols-rounded" style={{ fontSize: size, lineHeight: 1, color, userSelect: 'none', flexShrink: 0, ...style }}>{name}</span>
);

export const channelLabel = (c: Channel): string => CHANNEL_LABELS[c];

export const Chip: React.FC<{
  label: string;
  active?: boolean;
  onClick?: () => void;
  /** `success` = an already-satisfied channel: soft green fill, never the accent red. */
  tone?: 'accent' | 'neutral' | 'success';
  icon?: string;
  disabled?: boolean;
  small?: boolean;
}> = ({ label, active, onClick, tone = 'accent', icon, disabled, small }) => {
  const success = tone === 'success';
  const activeBg = tone === 'neutral' ? 'var(--t1)' : 'var(--accent)';
  // Success chips stay legible at full strength — they read as "done", not as an inert control.
  const border = success ? 'rgba(22,163,74,0.30)' : active ? 'transparent' : 'var(--card-border)';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: small ? '3px 8px' : '5px 11px',
        borderRadius: 999,
        border: `1px solid ${border}`,
        background: success ? 'rgba(22,163,74,0.12)' : active ? activeBg : 'var(--surface)',
        color: success ? 'var(--green)' : active ? '#fff' : 'var(--t2)',
        fontSize: small ? 11 : 12, fontWeight: 600, fontFamily: FONT,
        cursor: disabled ? 'default' : 'pointer', opacity: disabled && !success ? 0.5 : 1,
        transition: 'all .15s', whiteSpace: 'nowrap',
      }}
    >
      {icon && <Icon name={icon} size={small ? 13 : 14} />}
      {label}
    </button>
  );
};

export const PrimaryBtn: React.FC<{ children: React.ReactNode; onClick?: () => void; disabled?: boolean; icon?: string }> = ({ children, onClick, disabled, icon }) => (
  <button
    type="button" onClick={onClick} disabled={disabled}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      padding: '9px 16px', borderRadius: 'var(--radius-button)', border: 'none',
      background: disabled ? 'var(--card-border)' : 'var(--accent)', color: '#fff',
      fontSize: 13, fontWeight: 700, fontFamily: FONT, cursor: disabled ? 'default' : 'pointer',
      boxShadow: disabled ? 'none' : '0 2px 8px rgba(210,58,59,0.25)', transition: 'all .15s',
    }}
  >
    {icon && <Icon name={icon} size={16} />}
    {children}
  </button>
);

export const GhostBtn: React.FC<{ children: React.ReactNode; onClick?: () => void; danger?: boolean; icon?: string; small?: boolean; disabled?: boolean }> = ({ children, onClick, danger, icon, small, disabled }) => (
  <button
    type="button" onClick={onClick} disabled={disabled}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      padding: small ? '5px 10px' : '9px 14px', borderRadius: 'var(--radius-button)',
      border: `1px solid var(--card-border)`, background: 'var(--surface)',
      color: danger ? 'var(--red)' : 'var(--t2)', fontSize: small ? 12 : 13, fontWeight: 600,
      fontFamily: FONT, cursor: disabled ? 'default' : 'pointer', transition: 'all .15s',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {icon && <Icon name={icon} size={small ? 14 : 16} />}
    {children}
  </button>
);

export const Pill: React.FC<{ tone: 'ok' | 'warn' | 'bad' | 'info'; children: React.ReactNode }> = ({ tone, children }) => {
  const map = {
    ok: { c: 'var(--green)', b: 'rgba(22,163,74,0.10)' },
    warn: { c: 'var(--orange)', b: 'rgba(217,119,6,0.10)' },
    bad: { c: 'var(--red)', b: 'rgba(220,38,38,0.10)' },
    info: { c: 'var(--t2)', b: 'var(--bg)' },
  }[tone];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, color: map.c, background: map.b, fontFamily: FONT }}>
      {children}
    </span>
  );
};
